import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { RightOutlined, SearchOutlined } from '@ant-design/icons'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import axios from 'axios'

import type { PlaylistBrief, Song } from '../../utils/redux/playlistSlice'
import { useAudioPlayer } from '../../contexts/AudioPlayerContext'
import { useAppSelector } from '../../utils/redux/store'
import './SearchInput.scss'

const MIN_CHARS = 2
const LIMIT_SONGS_UI = 5
const LIMIT_PLAYLISTS_UI = 8

const PLAYLIST_ROUTE = (id: string) => `/playlist/${id}`

function stripFrDomain(input: string) {
  const s = String(input || '').trim()
  if (!s) return ''
  return s
    .replace('api2.fr.musictmm.com', 'api2.musictmm.com')
    .replace('://api2.fr.', '://api2.')
}

function ensureHttps(url: string) {
  const s0 = stripFrDomain(url)
  const s = String(s0 || '').trim()
  if (!s) return ''
  if (/^https?:\/\//i.test(s)) return s.replace(/\/$/, '')
  return `https://${s}`.replace(/\/$/, '')
}

function stripTrailingSlash(s: string) {
  return String(s || '').replace(/\/$/, '')
}

const API_BASE = ensureHttps(
  process.env.REACT_APP_API_BASE_URL ||
    process.env.REACT_APP_MUSIC_BASE_URL ||
    'https://api2.musictmm.com',
)

const UPLOADED_BASE = stripTrailingSlash(
  ensureHttps(process.env.REACT_APP_UPLOADED_BASE_URL || ''),
)

const musicApi = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
})

const SEARCH_PATH = '/song/search'

function norm(v: any) {
  return String(v ?? '')
    .trim()
    .toLowerCase()
}

function firstLetter(title?: string) {
  const t = String(title || '').trim()
  return t ? t[0].toUpperCase() : '?'
}

function uniqBy<T>(arr: T[], key: (x: T) => string) {
  const seen = new Set<string>()
  const out: T[] = []
  for (const item of arr) {
    const k = key(item)
    if (!k) continue
    if (seen.has(k)) continue
    seen.add(k)
    out.push(item)
  }
  return out
}

function resolvePlaylistImageUrl(image?: string) {
  if (!image) return ''
  const raw = String(image).trim()
  if (/^https?:\/\//i.test(raw)) return raw
  if (!UPLOADED_BASE) return ''
  const cleaned = raw.replace(/^\/+/, '').replace(/^playlists\//i, '')
  return `${UPLOADED_BASE}/playlists/${cleaned}`
}

function resolveSongImageUrl(image?: string) {
  if (!image) return ''
  const raw = String(image).trim()
  if (/^https?:\/\//i.test(raw)) return raw
  if (!UPLOADED_BASE) return ''
  const cleaned = raw.replace(/^\/+/, '').replace(/^uploaded\//i, '')
  return `${UPLOADED_BASE}/${cleaned}`
}

function resolveSongAudioUrl(v?: string) {
  if (!v) return ''
  const raw = String(v).trim()
  if (/^https?:\/\//i.test(raw)) return raw
  if (!UPLOADED_BASE) return raw
  const cleaned = raw.replace(/^\/+/, '')
  return `${UPLOADED_BASE}/${cleaned}`
}

function extractSongs(payload: any): any[] {
  if (Array.isArray(payload?.songs)) return payload.songs
  if (Array.isArray(payload?.data?.songs)) return payload.data.songs
  if (Array.isArray(payload)) return payload
  return []
}

function extractPlaylists(payload: any): any[] {
  if (Array.isArray(payload?.playlists)) return payload.playlists
  if (Array.isArray(payload?.data?.playlists)) return payload.data.playlists
  return []
}

type DropPos = { left: number; top: number; width: number; maxHeight: number }

type Props = {
  value?: string
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  placeholder?: string
}

export default function SearchInput(props: Props) {
  const { t } = useTranslation('search')
  const navigate = useNavigate()
  const location = useLocation()
  const { playQueue } = useAudioPlayer()

  const token = useAppSelector((s) => (s as any).auth?.token) as string | undefined
  const isOrdersPage = location.pathname.startsWith('/orders')

  const [value, setValue] = useState(() => String(props.value ?? ''))

  useEffect(() => {
    if (typeof props.value === 'string' && props.value !== value) {
      setValue(props.value)
    }
  }, [props.value, value])

  const rootRef = useRef<HTMLDivElement | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const dropdownRef = useRef<HTMLDivElement | null>(null)

  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState<DropPos | null>(null)

  const [songs, setSongs] = useState<Song[]>([])
  const [playlists, setPlaylists] = useState<PlaylistBrief[]>([])

  const [loading, setLoading] = useState(false)
  const [errSongs, setErrSongs] = useState<string | null>(null)
  const [errPlaylists, setErrPlaylists] = useState<string | null>(null)

  const abortRef = useRef<AbortController | null>(null)
  const reqIdRef = useRef(0)

  const cacheRef = useRef<Map<string, { songs: Song[]; playlists: PlaylistBrief[] }>>(
    new Map(),
  )

  const trimmed = value.trim()
  const query = norm(trimmed)

  const authHeaders = useMemo(() => {
    const tkn = String(token || '').trim()
    return tkn ? { Authorization: `Bearer ${tkn}` } : {}
  }, [token])

  async function searchAll(q: string, signal: AbortSignal) {
    const res = await musicApi.post(SEARCH_PATH, { q }, { signal, headers: authHeaders })
    return res.data
  }

  useEffect(() => {
    if (!open) return

    if (query.length < MIN_CHARS) {
      setSongs([])
      setPlaylists([])
      setErrSongs(null)
      setErrPlaylists(null)
      setLoading(false)
      abortRef.current?.abort()
      abortRef.current = null
      return
    }

    const cached = cacheRef.current.get(query)
    if (cached) {
      setSongs(cached.songs.slice(0, LIMIT_SONGS_UI))
      setPlaylists(cached.playlists.slice(0, LIMIT_PLAYLISTS_UI))
      setErrSongs(null)
      setErrPlaylists(null)
      return
    }

    const timer = window.setTimeout(() => {
      abortRef.current?.abort()
      const ac = new AbortController()
      abortRef.current = ac
      const myReqId = ++reqIdRef.current

      setLoading(true)
      setErrSongs(null)
      setErrPlaylists(null)

      void (async () => {
        try {
          const payload = await searchAll(trimmed, ac.signal)

          const songsRaw = extractSongs(payload)
          const playlistsRaw = extractPlaylists(payload)

          const normalizedSongs: Song[] = uniqBy(
            songsRaw.map((s: any) => {
              const img = resolveSongImageUrl(s?.imageUrl || s?.image || s?.cover || '')
              const audio = resolveSongAudioUrl(
                s?.audioUrl || s?.url || s?.src || s?.file || s?.path || '',
              )
              return {
                ...s,
                imageUrl: img || s?.imageUrl,
                audioUrl: audio || s?.audioUrl,
              } as Song
            }),
            (s: any) => String(s?._id ?? s?.id ?? `${s?.title}-${s?.artist}`),
          ).slice(0, LIMIT_SONGS_UI)

          const normalizedPlaylists: PlaylistBrief[] = uniqBy(
            playlistsRaw.map((p: any) => {
              const img = resolvePlaylistImageUrl(
                p?.image || p?.imageUrl || p?.cover || '',
              )
              return { ...p, image: img || p?.image } as PlaylistBrief
            }),
            (p: any) => String(p?._id ?? p?.id ?? p?.title ?? ''),
          ).slice(0, LIMIT_PLAYLISTS_UI)

          if (reqIdRef.current !== myReqId) return

          cacheRef.current.set(query, {
            songs: normalizedSongs,
            playlists: normalizedPlaylists,
          })

          setSongs(normalizedSongs)
          setPlaylists(normalizedPlaylists)
        } catch (e: any) {
          if (e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED') return
          const status = e?.response?.status
          if (reqIdRef.current !== myReqId) return

          setSongs([])
          setPlaylists([])

          if (status === 401) {
            setErrSongs(
              t('errors.unauthorized', {
                defaultValue: '401 Unauthorized (token invalid for this instance)',
              }),
            )
            setErrPlaylists(null)
          } else {
            setErrSongs(t('errors.search', { defaultValue: 'Search error' }))
            setErrPlaylists(null)
          }
        } finally {
          if (reqIdRef.current === myReqId) setLoading(false)
        }
      })()
    }, 250)

    return () => window.clearTimeout(timer)
  }, [open, query, trimmed, authHeaders, t])

  const hasAny = songs.length > 0 || playlists.length > 0
  const showDropdown = open && trimmed.length > 0

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const root = rootRef.current
      const dd = dropdownRef.current
      const target = e.target as Node
      if (!root) return
      const inRoot = root.contains(target)
      const inDD = !!dd && dd.contains(target)
      if (!inRoot && !inDD) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  const updatePos = () => {
    const el = inputRef.current
    if (!el) return
    const r = el.getBoundingClientRect()
    const gap = 10
    let left = r.left
    const top = r.bottom + gap
    const width = r.width
    const viewportPad = 12
    const maxLeft = window.innerWidth - width - viewportPad
    left = Math.max(viewportPad, Math.min(left, maxLeft))
    const maxHeight = Math.max(220, window.innerHeight - top - viewportPad)
    setPos({ left, top, width, maxHeight })
  }

  useLayoutEffect(() => {
    if (!open) return
    updatePos()
  }, [open, value])

  useEffect(() => {
    if (!open) return
    const onAny = () => updatePos()
    window.addEventListener('resize', onAny)
    window.addEventListener('scroll', onAny, true)
    return () => {
      window.removeEventListener('resize', onAny)
      window.removeEventListener('scroll', onAny, true)
    }
  }, [open])

  const onFocus = () => setOpen(true)

  const goPlaylist = (id: string) => {
    setOpen(false)
    navigate(PLAYLIST_ROUTE(id))
  }

  const onClickSong = (song: Song) => {
    setOpen(false)
    playQueue([song], 0)
  }

  const rowKeyDown =
    (onActivate: () => void) => (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        onActivate()
      }
    }

  const onKeyDownInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return

    const pid = (playlists[0] as any)?._id || (playlists[0] as any)?.id
    if (pid) {
      e.preventDefault()
      goPlaylist(String(pid))
      return
    }
    if (songs.length > 0) {
      e.preventDefault()
      onClickSong(songs[0])
      return
    }

    setOpen(false)
  }

  const dropdown =
    showDropdown && pos
      ? createPortal(
          <div
            ref={dropdownRef}
            className="home-search__dropdown fixedLeft"
            style={{
              position: 'fixed',
              left: pos.left,
              top: pos.top,
              width: pos.width,
              maxHeight: pos.maxHeight,
            }}
          >
            <div className="home-search__dropdownInner">
              {!hasAny && query.length >= MIN_CHARS && loading ? (
                <div className="home-search__empty">
                  {t('states.loading', { defaultValue: 'Loading…' })}
                </div>
              ) : null}

              {query.length >= MIN_CHARS ? (
                <div className="home-search__section">
                  <div className="home-search__sectionTitle">
                    {t('sections.songs', { defaultValue: 'Songs' })}
                  </div>

                  {errSongs ? (
                    <div className="home-search__empty">{errSongs}</div>
                  ) : loading && songs.length === 0 ? (
                    <div className="home-search__empty">
                      {t('states.loading', { defaultValue: 'Loading…' })}
                    </div>
                  ) : songs.length === 0 ? (
                    <div className="home-search__empty">
                      {t('states.nothingFound', { defaultValue: 'Nothing found' })}
                    </div>
                  ) : (
                    songs.map((s: any) => {
                      const title = String(s?.title ?? '—')
                      const meta = String(s?.artist ?? s?.author ?? '')
                      const img = resolveSongImageUrl(s?.imageUrl || s?.image)

                      return (
                        <div
                          key={String(s?._id ?? s?.id ?? title)}
                          className="home-search__row"
                          role="button"
                          tabIndex={0}
                          onMouseDown={(ev) => ev.preventDefault()}
                          onClick={() => onClickSong(s)}
                          onKeyDown={rowKeyDown(() => onClickSong(s))}
                        >
                          <span className="home-search__playlistAvatar">
                            <span className="home-search__avatarLetter">
                              {firstLetter(title)}
                            </span>
                            {img ? (
                              <img
                                src={img}
                                alt=""
                                loading="lazy"
                                referrerPolicy="no-referrer"
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none'
                                }}
                              />
                            ) : null}
                          </span>

                          <span className="home-search__rowMain">
                            <span className="home-search__title">{title}</span>
                            {meta ? (
                              <span className="home-search__subtitle">{meta}</span>
                            ) : null}
                          </span>

                          <span className="home-search__rowRight">
                            <RightOutlined />
                          </span>
                        </div>
                      )
                    })
                  )}
                </div>
              ) : null}

              {query.length >= MIN_CHARS ? (
                <div className="home-search__section">
                  <div className="home-search__sectionTitle">
                    {t('sections.playlists', { defaultValue: 'Playlists' })}
                  </div>

                  {errPlaylists ? (
                    <div className="home-search__empty">{errPlaylists}</div>
                  ) : loading && playlists.length === 0 ? (
                    <div className="home-search__empty">
                      {t('states.loading', { defaultValue: 'Loading…' })}
                    </div>
                  ) : playlists.length === 0 ? (
                    <div className="home-search__empty">
                      {t('states.nothingFound', { defaultValue: 'Nothing found' })}
                    </div>
                  ) : (
                    playlists.map((p: any) => {
                      const pid = String(p?._id ?? p?.id ?? '')
                      const title = String(p?.title ?? '—')
                      const img = resolvePlaylistImageUrl(p?.image)

                      return (
                        <div
                          key={pid || title}
                          className="home-search__row"
                          role="button"
                          tabIndex={0}
                          onMouseDown={(ev) => ev.preventDefault()}
                          onClick={() => pid && goPlaylist(pid)}
                          onKeyDown={rowKeyDown(() => pid && goPlaylist(pid))}
                        >
                          <span className="home-search__playlistAvatar">
                            <span className="home-search__avatarLetter">
                              {firstLetter(title)}
                            </span>
                            {img ? (
                              <img
                                src={img}
                                alt=""
                                loading="lazy"
                                referrerPolicy="no-referrer"
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none'
                                }}
                              />
                            ) : null}
                          </span>

                          <span className="home-search__rowMain">
                            <span className="home-search__title">{title}</span>
                          </span>

                          <span className="home-search__rowRight">
                            <RightOutlined />
                          </span>
                        </div>
                      )
                    })
                  )}
                </div>
              ) : null}

              {trimmed.length > 0 && query.length < MIN_CHARS ? (
                <div className="home-search__empty">
                  {t('states.minChars', {
                    count: MIN_CHARS,
                    defaultValue: 'Type at least {{count}} letters',
                  })}
                </div>
              ) : null}
            </div>
          </div>,
          document.body,
        )
      : null

  return (
    <div className="home-search" ref={rootRef}>
      <SearchOutlined className="home-search__icon" />

      <input
        ref={inputRef}
        className="home-search__input"
        value={value}
        onChange={(e) => {
          setValue(e.target.value)
          if (!isOrdersPage) props.onChange?.(e)
          if (!open) setOpen(true)
        }}
        onFocus={onFocus}
        onKeyDown={onKeyDownInput}
        placeholder={
          props.placeholder ??
          t('placeholder', { defaultValue: 'Search songs, albums, artists, podcasts' })
        }
        spellCheck={false}
      />

      {dropdown}
    </div>
  )
}
